import { useState, useEffect, useRef, useCallback } from 'react';
import { createKalshiClient } from '../../lib/kalshiApi';
import {
  createNbaStream,
  createConsolidatedNbaStream,
  getMarketState,
  type MarketPriceData,
} from '../../lib/market/stream';
import {
  createSportsStream,
  type SportsStream,
  type GameData,
  type SportsLoadingState,
} from '../../lib/sportsStream';
import { type Sport, type MarketType } from '../../lib/sportsDiscovery';
import { initAuth } from '../../lib/kalshiApi/kalshiAuth';
import { createMarketChart, type ChartManager } from '../../lib/market/chartManager';
import { PositionTracker } from '../../lib/trading/positionTracker';
import { ReconnectState } from '../../lib/infra/reconnect';
import type { DataMode } from '../../lib/infra/dataMode';
import type { NBAMarketRow, StreamUpdate, KalshiCredentials } from '../../types';
import type { ConsolidatedGameBooks } from '../../lib/nbaConsolidated/types';
import { boundaryWarn } from '../../lib/infra/boundaryLog';

/** Shallow-compare two games arrays by length, keys, and venue snapshot timestamps. */
function gamesChanged(prev: ConsolidatedGameBooks[], next: ConsolidatedGameBooks[]): boolean {
  if (next.length !== prev.length) return true;
  for (let i = 0; i < next.length; i++) {
    const g = next[i];
    const p = prev[i];
    if (
      g.key !== p.key ||
      g.kalshi?.tsMs !== p.kalshi?.tsMs ||
      g.polymarket?.tsMs !== p.polymarket?.tsMs
    )
      return true;
  }
  return false;
}

export interface StreamManagementParams {
  isConnected: boolean;
  apiClientRef: React.MutableRefObject<Awaited<ReturnType<typeof createKalshiClient>> | null>;
  credentials: KalshiCredentials | null;
  isMultiTenant?: boolean;
  dataMode: DataMode;
  chartPeriod: '1m' | '5m' | '15m' | '1h' | '1d';
  positionTrackerRef: React.MutableRefObject<PositionTracker>;
  refreshPositions: () => void;
  marketsDataRef: React.MutableRefObject<Map<string, NBAMarketRow>>;
  setIsConnected: React.Dispatch<React.SetStateAction<boolean>>;
  setConnectionStatus: React.Dispatch<React.SetStateAction<string>>;
  /** Callback to record feed message arrival for staleness tracking */
  onFeedMessage?: () => void;
}

export interface StreamManagementReturn {
  markets: NBAMarketRow[];
  consolidatedGames: ConsolidatedGameBooks[];
  consolidatedLoading: boolean;
  sportsGames: Map<string, GameData[]>;
  sportsLoading: Map<string, boolean>;
  sportsLoadingStates: Map<string, SportsLoadingState>;
  feedStatus: 'live' | 'offline';
  lastUpdateTime: string;
  /** Number of reconnection attempts since last successful connection */
  reconnectAttempt: number;
  handleSportMarketChange: (sport: Sport, marketType: MarketType) => Promise<void>;
  cleanupStreams: () => void;
}

export function useStreamManagement({
  isConnected,
  apiClientRef,
  credentials,
  isMultiTenant,
  dataMode,
  chartPeriod,
  positionTrackerRef,
  refreshPositions,
  marketsDataRef,
  setIsConnected,
  setConnectionStatus,
  onFeedMessage,
}: StreamManagementParams): StreamManagementReturn {
  const [markets, setMarkets] = useState<NBAMarketRow[]>([]);
  const [consolidatedGames, setConsolidatedGames] = useState<ConsolidatedGameBooks[]>([]);
  const [consolidatedLoading, setConsolidatedLoading] = useState(false);
  const [sportsGames, setSportsGames] = useState<Map<string, GameData[]>>(new Map());
  const [sportsLoading, setSportsLoading] = useState<Map<string, boolean>>(new Map());
  const [sportsLoadingStates, setSportsLoadingStates] = useState<Map<string, SportsLoadingState>>(
    new Map()
  );
  const [feedStatus, setFeedStatus] = useState<'live' | 'offline'>('offline');
  const [lastUpdateTime, setLastUpdateTime] = useState('--:--:--');

  // Smart-relay auto-fallback: if relay doesn't deliver data within timeout, fall back to proxy
  const [smartRelayFailed, setSmartRelayFailed] = useState(false);
  // MT mode: prefer smart-relay (relay provides all market data). Falls back to proxy on failure.
  const effectiveDataMode =
    isMultiTenant && !smartRelayFailed
      ? 'smart-relay'
      : dataMode === 'smart-relay' && smartRelayFailed
        ? 'proxy'
        : dataMode;

  const streamRef = useRef<ReturnType<typeof createNbaStream> | null>(null);
  const consolidatedStreamRef = useRef<ReturnType<typeof createConsolidatedNbaStream> | null>(null);
  const marketChartsRef = useRef<Map<string, ChartManager>>(new Map());
  const marketChartsContainerRef = useRef<HTMLDivElement>(null);
  const sportsStreamsMapRef = useRef<Map<string, SportsStream>>(new Map());
  const prevGamesRef = useRef<ConsolidatedGameBooks[]>([]);
  const sportsStreamKeyRef = useRef<string>('');
  const reconnectStateRef = useRef(new ReconnectState());
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout>>();
  // Stream health: last message timestamp for staleness detection
  const lastConsolidatedMsgRef = useRef<number>(0);
  // Hydration sequence counter for diagnosing REST vs WS ordering
  const hydrationSeqRef = useRef(0);
  // Bump this to force the stream init effect to re-run
  const [streamGeneration, setStreamGeneration] = useState(0);

  // Reset fallback flag when user explicitly changes dataMode (e.g. toggles in Settings)
  const prevDataModeRef = useRef(dataMode);
  if (dataMode !== prevDataModeRef.current) {
    prevDataModeRef.current = dataMode;
    if (smartRelayFailed) setSmartRelayFailed(false);
  }

  // Update market charts
  const updateMarketCharts = useCallback(() => {
    const marketState = getMarketState();
    if (!marketState || !marketChartsContainerRef.current) return;

    markets.forEach((market) => {
      let chart = marketChartsRef.current.get(market.market_ticker);
      if (!chart) {
        const container = document.createElement('div');
        container.className = 'market-chart-item';
        marketChartsContainerRef.current!.appendChild(container);
        chart = createMarketChart(container, true);
        chart.init();
        marketChartsRef.current.set(market.market_ticker, chart);
      }

      if (chart) {
        const candles = marketState.getCandles(market.market_ticker, chartPeriod, 100);
        if (candles.length > 0) {
          chart.updateData(candles);
        }
      }
    });
  }, [markets, chartPeriod]);

  // Handle stream updates
  const handleStreamUpdate = useCallback(
    (update: StreamUpdate) => {
      if (update.type === 'snapshot') {
        setMarkets(update.rows || []);
        const dataMap = new Map<string, NBAMarketRow>();
        (update.rows || []).forEach((row) => {
          dataMap.set(row.market_ticker, row);
        });
        marketsDataRef.current = dataMap;
        refreshPositions();
        updateMarketCharts();
      } else if (update.type === 'update') {
        setMarkets((prev) => {
          const updated = new Map(prev.map((m) => [m.market_ticker, m]));
          (update.rows || []).forEach((row) => {
            updated.set(row.market_ticker, row);
          });
          return Array.from(updated.values());
        });
      } else if (update.type === 'price_update' && update.priceUpdate) {
        setMarkets((prev) =>
          prev.map((m) => {
            if (m.market_ticker === update.priceUpdate!.ticker) {
              const updated = { ...m, currentPrice: update.priceUpdate!.price };
              marketsDataRef.current.set(m.market_ticker, updated);
              return updated;
            }
            return m;
          })
        );

        const priceMap = new Map<string, number>();
        priceMap.set(update.priceUpdate.ticker, update.priceUpdate.price);
        positionTrackerRef.current.updatePrices(priceMap);
        refreshPositions();
        updateMarketCharts();
      }

      setIsConnected(true);
      setConnectionStatus('Connected');
      setFeedStatus('live');
      onFeedMessage?.();
      reconnectStateRef.current.onMessage();

      const now = new Date();
      setLastUpdateTime(
        now.toLocaleTimeString('en-US', {
          hour12: true,
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        })
      );
    },
    [
      refreshPositions,
      updateMarketCharts,
      marketsDataRef,
      positionTrackerRef,
      setIsConnected,
      setConnectionStatus,
      onFeedMessage,
    ]
  );

  // Handle market data from smart relay — populates marketsDataRef + positionTracker
  const handleSmartRelayMarketData = useCallback(
    (data: Map<string, MarketPriceData>) => {
      const priceMap = new Map<string, number>();
      for (const [ticker, md] of data) {
        const existing = marketsDataRef.current.get(ticker);
        const price = md.price ?? undefined;
        const yesBid = md.yesBidCents ?? undefined;
        const yesAsk = md.yesAskCents ?? undefined;

        const row: NBAMarketRow = existing
          ? {
              ...existing,
              currentPrice: price,
              awayYesBid: yesBid ?? existing.awayYesBid,
              awayYesAsk: yesAsk ?? existing.awayYesAsk,
            }
          : {
              market_ticker: ticker,
              gameDate: '',
              gameTime: '',
              awayTeam: '',
              homeTeam: '',
              awayProb: null,
              homeProb: null,
              awayLiq: 0,
              homeLiq: 0,
              sortKey: ticker,
              currentPrice: price,
              awayYesBid: yesBid,
              awayYesAsk: yesAsk,
            };
        marketsDataRef.current.set(ticker, row);

        if (md.price !== null) {
          priceMap.set(ticker, md.price);
        }
      }

      if (priceMap.size > 0) {
        positionTrackerRef.current.updatePrices(priceMap);
        refreshPositions();
      }
    },
    [marketsDataRef, positionTrackerRef, refreshPositions]
  );

  // Init streams when connected (re-runs on streamGeneration bump for reconnects)
  useEffect(() => {
    console.warn('[stream-init] effect fired', {
      isConnected,
      hasApi: !!apiClientRef.current,
      credentials: !!credentials,
      isMultiTenant,
      effectiveDataMode,
      hasStream: !!streamRef.current,
      hasConsolidated: !!consolidatedStreamRef.current,
      streamGeneration,
    });

    if (!isConnected || !apiClientRef.current) {
      console.warn('[stream-init] BAIL: not connected or no api');
      return;
    }
    if (!credentials && !isMultiTenant) {
      console.warn('[stream-init] BAIL: no credentials and not MT');
      return;
    }

    // Already have streams
    if (streamRef.current || consolidatedStreamRef.current) {
      console.warn('[stream-init] BAIL: streams already exist');
      return;
    }

    console.warn('[stream-init] PROCEEDING — will create streams');

    let cancelled = false;
    const apiClient = apiClientRef.current;
    const isSmartRelay = effectiveDataMode === 'smart-relay';

    // Smart-relay fallback: if no data arrives within 8s, tear down and retry as proxy
    let smartRelayFallbackTimer: ReturnType<typeof setTimeout> | undefined;
    let smartRelayDataReceived = false;
    const clearFallbackTimer = () => {
      if (smartRelayFallbackTimer) {
        clearTimeout(smartRelayFallbackTimer);
        smartRelayFallbackTimer = undefined;
      }
    };

    if (isSmartRelay) {
      smartRelayFallbackTimer = setTimeout(() => {
        if (smartRelayDataReceived || cancelled) return;
        console.warn('[stream] Smart relay did not deliver data within 8s — falling back to proxy');
        if (consolidatedStreamRef.current) {
          consolidatedStreamRef.current.stop();
          consolidatedStreamRef.current = null;
        }
        if (streamRef.current) {
          streamRef.current.stop();
          streamRef.current = null;
        }
        setSmartRelayFailed(true);
        setStreamGeneration((g) => g + 1);
      }, 8000);
    }

    (async () => {
      try {
        // ST mode: browser signs WS requests with user keys
        // MT mode: no user keys — skip initAuth and per-user WS entirely
        let cryptoKey: CryptoKey | undefined;

        if (credentials) {
          const auth = await initAuth(credentials.accessKeyId, credentials.privateKeyPem);
          cryptoKey = auth.privateKey;

          if (cancelled) return;

          // In proxy mode, create the NBA stream (user-key REST + WS for all data)
          // In smart-relay mode, skip this — the relay provides bulk market data
          if (!isSmartRelay) {
            const stream = createNbaStream({
              api: apiClient,
              accessKeyId: credentials.accessKeyId,
              privateKey: cryptoKey,
              environment: credentials.environment,
              useRelay: true,
              useMock: false,
            });

            streamRef.current = stream;
            stream.onUpdate((update: StreamUpdate) => {
              handleStreamUpdate(update);
            });

            try {
              stream.start();
              reconnectStateRef.current.onConnected();
            } catch (err) {
              console.error('Stream start failed:', err);
            }
          }
        }

        // Create consolidated stream (uses smart relay in smart-relay mode, REST in proxy mode)
        console.warn('[stream-init] Creating consolidated stream', {
          dataMode: effectiveDataMode,
          isSmartRelay,
          hasCreds: !!credentials,
        });
        try {
          setConsolidatedLoading(true);
          const consolidated = createConsolidatedNbaStream({
            api: apiClient,
            accessKeyId: credentials?.accessKeyId,
            privateKey: cryptoKey,
            environment: credentials?.environment ?? 'prod',
            useRelay: true,
            useMock: false,
            dataMode: effectiveDataMode,
          });
          consolidatedStreamRef.current = consolidated;

          consolidated.onUpdate(({ games, lastUpdateMs: updateMs }) => {
            smartRelayDataReceived = true;
            clearFallbackTimer();
            setConsolidatedLoading(false);
            lastConsolidatedMsgRef.current = Date.now();
            // Hydration sequence log
            const seq = ++hydrationSeqRef.current;
            if (seq <= 5) {
              boundaryWarn('hydration', `[${seq}] Consolidated update`, {
                count: games.length,
                phase: 'ws-update',
                hasExistingData: prevGamesRef.current.length > 0,
              });
            }
            // Only trigger re-render if games actually changed
            if (gamesChanged(prevGamesRef.current, games)) {
              prevGamesRef.current = games;
              setConsolidatedGames(games);
            }
            setFeedStatus('live');
            setIsConnected(true);
            setConnectionStatus('Connected');
            onFeedMessage?.();
            reconnectStateRef.current.onMessage();
            const now = new Date(updateMs || Date.now());
            setLastUpdateTime(
              now.toLocaleTimeString('en-US', {
                hour12: true,
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
              })
            );
          });

          // In smart-relay mode, wire up market data for marketsDataRef + positionTracker
          if (isSmartRelay && consolidated.onMarketData) {
            consolidated.onMarketData(handleSmartRelayMarketData);
          }

          if (isSmartRelay && consolidated.onReady) {
            consolidated.onReady((isReady) => {
              if (isReady) {
                smartRelayDataReceived = true;
                clearFallbackTimer();
                setConsolidatedLoading(false);
                reconnectStateRef.current.onConnected();
              } else {
                setConsolidatedLoading(true);
              }
            });
          }

          consolidated.start();

          if (isSmartRelay) {
            reconnectStateRef.current.onConnected();
          }
        } catch (err) {
          console.warn('Consolidated NBA stream unavailable:', err);
          setConsolidatedGames([]);
          setConsolidatedLoading(false);
          clearFallbackTimer();
        }
      } catch (err) {
        console.error('Stream auth init failed:', err);
        clearFallbackTimer();
      }
    })();

    return () => {
      cancelled = true;
      clearFallbackTimer();
    };
  }, [
    isConnected,
    credentials,
    isMultiTenant,
    apiClientRef,
    effectiveDataMode,
    handleStreamUpdate,
    handleSmartRelayMarketData,
    streamGeneration,
  ]);

  // Auto-reconnect: when feed goes offline, tear down and rebuild streams with backoff
  useEffect(() => {
    if (!isConnected || feedStatus !== 'offline') return;
    // Don't reconnect if streams were never started (initial load)
    if (!reconnectStateRef.current.lastMessageMs) return;

    const { nextDelayMs } = reconnectStateRef.current.onDisconnected();
    if (import.meta.env.DEV)
      console.log(
        `[reconnect] Feed offline — reconnecting in ${nextDelayMs}ms (attempt ${reconnectStateRef.current.attempt})`
      );

    reconnectTimerRef.current = setTimeout(() => {
      // Tear down existing streams so the init effect will re-create them
      if (streamRef.current) {
        streamRef.current.stop();
        streamRef.current = null;
      }
      if (consolidatedStreamRef.current) {
        consolidatedStreamRef.current.stop();
        consolidatedStreamRef.current = null;
      }
      // Bump generation to trigger the init effect
      setStreamGeneration((g) => g + 1);
    }, nextDelayMs);

    return () => clearTimeout(reconnectTimerRef.current);
  }, [isConnected, feedStatus]);

  // Stream health: detect staleness and set feed offline if no data for 30s
  useEffect(() => {
    if (!isConnected) return;
    const interval = setInterval(() => {
      const last = lastConsolidatedMsgRef.current;
      if (last > 0) {
        const age = Date.now() - last;
        if (age > 30_000) {
          setFeedStatus('offline');
          boundaryWarn(
            'stream',
            `Consolidated stream stale: ${Math.round(age / 1000)}s since last msg`
          );
        }
      }
    }, 10_000);
    return () => clearInterval(interval);
  }, [isConnected]);

  // Update charts when period or markets change
  useEffect(() => {
    if (isConnected) {
      updateMarketCharts();
    }
  }, [chartPeriod, markets, isConnected, updateMarketCharts]);

  // Handler for sport/market type changes
  // Streams are cached: switching back to a previously loaded market type is instant
  const handleSportMarketChange = useCallback(
    async (sport: Sport, marketType: MarketType) => {
      if (sport === 'nba' && marketType === 'moneyline') return;

      const streamKey = `${sport}-${marketType}`;
      if (sportsStreamKeyRef.current === streamKey) return;

      sportsStreamKeyRef.current = streamKey;

      // If this stream is already running in the cache, reuse it (instant switch)
      if (sportsStreamsMapRef.current.has(streamKey)) return;

      if (!apiClientRef.current || !credentials) return;

      setSportsLoading((prev) => new Map(prev).set(streamKey, true));
      setSportsLoadingStates((prev) => {
        const next = new Map(prev);
        const now = Date.now();
        next.set(streamKey, {
          isLoading: true,
          phase: 'discovering-markets',
          startedAtMs: now,
          updatedAtMs: now,
          events: [
            {
              tsMs: now,
              phase: 'discovering-markets',
              level: 'info',
              message: `Starting ${sport.toUpperCase()} ${marketType} stream`,
            },
          ],
        });
        return next;
      });

      try {
        const { privateKey: cryptoKey } = await initAuth(
          credentials.accessKeyId,
          credentials.privateKeyPem
        );

        const stream = createSportsStream(
          {
            api: apiClientRef.current,
            accessKeyId: credentials.accessKeyId,
            privateKey: cryptoKey,
            environment: credentials.environment,
            useRelay: true,
            useMock: false,
          },
          {
            sport,
            marketType,
            daysAhead: sport === 'cbb' ? 3 : 30,
          }
        );

        sportsStreamsMapRef.current.set(streamKey, stream);

        stream.onUpdate((update) => {
          setSportsGames((prev) => {
            const next = new Map(prev);
            next.set(streamKey, update.games);
            return next;
          });
          setSportsLoadingStates((prev) => {
            const next = new Map(prev);
            next.set(streamKey, update.loadingState);
            return next;
          });
          setSportsLoading((prev) => {
            const next = new Map(prev);
            next.set(streamKey, update.loadingState.isLoading);
            return next;
          });
        });

        await stream.start();
      } catch (err) {
        console.error(`Failed to start ${streamKey} stream:`, err);
        sportsStreamsMapRef.current.delete(streamKey);
        setSportsLoadingStates((prev) => {
          const next = new Map(prev);
          const now = Date.now();
          const message = err instanceof Error ? err.message : String(err);
          next.set(streamKey, {
            isLoading: false,
            phase: 'error',
            startedAtMs: now,
            updatedAtMs: now,
            events: [
              {
                tsMs: now,
                phase: 'error',
                level: 'error',
                message,
              },
            ],
          });
          return next;
        });
        setSportsLoading((prev) => {
          const next = new Map(prev);
          next.set(streamKey, false);
          return next;
        });
      }
    },
    [credentials, apiClientRef]
  );

  const cleanupStreams = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.stop();
      streamRef.current = null;
    }
    if (consolidatedStreamRef.current) {
      consolidatedStreamRef.current.stop();
      consolidatedStreamRef.current = null;
    }
    for (const stream of sportsStreamsMapRef.current.values()) {
      stream.stop();
    }
    sportsStreamsMapRef.current.clear();
    sportsStreamKeyRef.current = '';
    clearTimeout(reconnectTimerRef.current);
    reconnectStateRef.current.reset();

    // Clean up charts
    marketChartsRef.current.forEach((chart) => chart.destroy());
    marketChartsRef.current.clear();

    setMarkets([]);
    setConsolidatedGames([]);
    setConsolidatedLoading(false);
    setSportsGames(new Map());
    setSportsLoading(new Map());
    setSportsLoadingStates(new Map());
    setFeedStatus('offline');
    setLastUpdateTime('--:--:--');
  }, []);

  return {
    markets,
    consolidatedGames,
    consolidatedLoading,
    sportsGames,
    sportsLoading,
    sportsLoadingStates,
    feedStatus,
    lastUpdateTime,
    reconnectAttempt: reconnectStateRef.current.attempt,
    handleSportMarketChange,
    cleanupStreams,
  };
}
